import { StateCreator } from "zustand"
import {
    gerarCorpoHTML,
    gerarTextoImpressao,
} from "../pages/RecebedorDePedidos/gerarTextoImpressao"
import { imprimirCode, imprimirHTML } from "../pages/RecebedorDePedidos/qzConfig"
import {
    getConfiguracoesDeTextoLocal,
    setConfiguracoesDeTextoLocal,
} from "../pages/textoImpressao/context/textoImpressaoLocal"
import { alertStart } from "../routes"
import { RegistrarLogErro } from "../services/clientefiel"
import { configurarSegurancaQz } from "../services/qzSeguranca"
import { StoreType } from "./AppStore"
import { encaminharImpressao, iniciarImpressoraLider, souImpressoraLider } from "./impressaoLider"

// [IMPRESSAO-DEBUG] Log temporário para diagnosticar o caminho da impressão
// (QZ vs navegador, aba líder). REMOVER quando terminar o diagnóstico.
function logImp(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.log("%c[IMPRESSAO]", "color:#e67e22;font-weight:bold", ...args)
}

/**
 * Imprime a comanda numa JANELA SEPARADA (nova aba/pop-up), não na página do painel.
 *
 * Motivo: `window.print()` é síncrono e bloqueia a thread da janela que o chama. Se for
 * a aba do painel, a operação inteira congela até o diálogo fechar — e, com drivers
 * problemáticos (ex.: PDFCreator), a pré-visualização trava e a página fica inutilizável.
 * Imprimindo numa janela dedicada, só ela bloqueia; o painel segue respondendo, e a
 * janela se fecha sozinha ao terminar (ou o usuário fecha a aba sem afetar o painel).
 *
 * Precisa ser chamado a partir de um gesto do usuário (clique) para o navegador não
 * bloquear o pop-up — hoje toda impressão pelo navegador é acionada por clique.
 */
function imprimirEmNovaAba(conteudoHtml: string, tamanhoFonte: string): void {
    let janela: Window | null = null
    try {
        // Aba normal (sem width/height): uma "popup window" com features é bloqueada com
        // mais frequência; como aba comum, o navegador libera no gesto do usuário.
        janela = window.open("", "_blank")
    } catch {
        janela = null
    }

    // Pop-up/aba bloqueada pelo navegador. NUNCA cair para window.print() na própria
    // página — é justamente o que congela o painel (a thread da aba bloqueia até o
    // diálogo fechar, e drivers ruins prendem a pré-visualização). Melhor avisar o
    // usuário para liberar o pop-up e não imprimir agora do que travar tudo.
    if (!janela) {
        alertStart(
            "O navegador bloqueou a janela de impressão. Permita pop-ups para este site (ícone na barra de endereço) e tente imprimir novamente.",
            "warning"
        )
        return
    }

    janela.document.open()
    janela.document.write(
        '<!doctype html><html><head><meta charset="utf-8"><title>Comanda</title>' +
            "<style>" +
            "@page{margin:6mm}" +
            `body{margin:0;color:#000;font-weight:800;font-size:${tamanhoFonte};line-height:1.3}` +
            ".quebra-pagina{page-break-after:always}" +
            "</style></head><body>" +
            conteudoHtml +
            // Imprime assim que a janela carrega e se fecha ao terminar/cancelar. O
            // setTimeout é rede de segurança caso onafterprint não dispare no navegador.
            "<script>window.onload=function(){window.focus();window.print();};" +
            "window.onafterprint=function(){window.close();};" +
            "setTimeout(function(){try{window.close();}catch(e){}},60000);</script>" +
            "</body></html>"
    )
    janela.document.close()
}

const qz = require("qz-tray")

// ── QZ realmente conectado? ──────────────────────────────────────────────────
// O `qz.websocket.isActive()` do qz-tray retorna true também no estado CONNECTING
// (ver qz-tray.js: readyState === OPEN || CONNECTING). Então, enquanto o painel
// tenta reconectar num QZ FECHADO (ciclando as portas 8181/8182/8283…), isActive()
// diz "ativo" — e a impressão era roteada para um QZ morto, sem cair no fallback do
// navegador. Mantemos um flag próprio que só fica true depois de ENUMERAR impressoras
// com sucesso (conexão de fato aberta) e volta a false ao fechar/errar. É a fonte
// confiável de "QZ utilizável".
let qzConexaoViva = false

// Busca de impressoras em andamento (single-flight): vários caminhos pedem a conexão
// quase ao mesmo tempo e dois `connect()` simultâneos derrubam um ao outro. Enquanto
// houver uma busca viva, todo mundo espera a MESMA promise.
let buscaImpressorasEmAndamento: Promise<any[]> | null = null

// Quando o QZ está FECHADO, o `qz.websocket.connect()` do qz-tray fica ciclando várias
// portas (8181/8182/8283/8384/8485/localhost.qz.io…), cada uma com timeout, e demora
// muitos segundos até desistir. Guardamos o instante da última falha para NÃO tentar
// reconectar inline (segurando o aceite/preparo) logo em seguida — o polling do
// Cabeçalho reconecta em segundo plano.
let ultimaFalhaQzEm = 0
// Teto de espera ao tentar religar o QZ dentro do fluxo de aceite/preparo: se ele
// voltou, conecta bem antes disso; se está fechado, seguimos sem travar o spinner.
const RECONEXAO_QZ_TIMEOUT_MS = 1500
const JANELA_SEM_RETENTAR_QZ_MS = 30000

// Status que o painel esconde (não confirmados) e que também NÃO podem imprimir comanda:
// -5 = FALHA_PAGAMENTO · 77 = AGUARDANDO_PAGAMENTO (Pix/cartão online não pago) ·
// 99 = CADASTRADO (criado, não confirmado). Espelha `STATUS_OCULTOS_DO_PAINEL` do
// PedidosStore (mantido local para evitar import circular: PedidosStore já importa deste
// store). O backend não deveria emitir PRINT para esses status, mas o ramo PRINT confia no
// evento — esta é a defesa em profundidade.
const STATUS_OCULTOS_SEM_IMPRESSAO = [-5, 77, 99]

// Segurança do QZ: assina as requisições com o certificado confiável, evitando o
// prompt/bloqueio ("Connection blocked by client") que impedia o reconhecimento do
// QZ. Configurado uma vez no carregamento do store.
configurarSegurancaQz()

// ── Preferência POR NAVEGADOR: forçar impressão nativa do navegador mesmo com o QZ
// disponível. O padrão é auto-detectar (QZ presente → "plugin"); esta flag permite o
// usuário voltar para "navegador" nesta estação, sem afetar as demais. ──
const LS_FORCAR_NAVEGADOR = "forcarImpressaoNavegador"

export const forcarImpressaoNavegador = (): boolean => {
    try {
        return localStorage.getItem(LS_FORCAR_NAVEGADOR) === "1"
    } catch {
        return false
    }
}

export const setForcarImpressaoNavegador = (forcar: boolean): void => {
    try {
        if (forcar) localStorage.setItem(LS_FORCAR_NAVEGADOR, "1")
        else localStorage.removeItem(LS_FORCAR_NAVEGADOR)
    } catch {
        /* ignore */
    }
}

// ── Resync: pedidos já resolvidos (impressos ou dispensados) pelo diálogo de
// recuperação que aparece ao abrir/atualizar o Recebedor. Guardado por navegador
// (cada estação decide o que já tratou); janela de 24h para não crescer. Serve só
// para NÃO reofertar um pedido que o usuário já mandou imprimir mas que segue em
// situação 0 (imprimir ≠ aceitar). ──
const LS_RESYNC_RESOLVIDOS = "pedidosResyncResolvidos"
const JANELA_RESYNC_MS = 24 * 60 * 60 * 1000

const lerResyncResolvidos = (): Record<string, number> => {
    try {
        return JSON.parse(localStorage.getItem(LS_RESYNC_RESOLVIDOS) || "{}")
    } catch {
        return {}
    }
}
export const resyncJaResolvido = (id: number | string): boolean => {
    const mapa = lerResyncResolvidos()
    const t = mapa[String(id)]
    return t != null && Date.now() - t < JANELA_RESYNC_MS
}
export const marcarResyncResolvido = (id: number | string) => {
    try {
        const mapa = lerResyncResolvidos()
        const agora = Date.now()
        mapa[String(id)] = agora
        for (const k of Object.keys(mapa)) {
            if (agora - mapa[k] > JANELA_RESYNC_MS) delete mapa[k]
        }
        localStorage.setItem(LS_RESYNC_RESOLVIDOS, JSON.stringify(mapa))
    } catch {
        /* ignore */
    }
}

export type ImpressaoStoreType = {
    setState: (state: Partial<ImpressaoStoreType>) => void

    impressorasQZ: string[]
    buscarImpressoras: () => Promise<string[]>
    /** True se há conexão ATIVA e estabelecida com o QZ (não fica preso em cache). */
    qzAtivo: () => boolean
    /**
     * Tenta religar o QZ SEM travar a operação: pula se ele falhou há pouco (o polling
     * do Cabeçalho reconecta em 2º plano) e, quando tenta, limita a espera. Usado no
     * aceite/preparo para não segurar o spinner enquanto o QZ (fechado) cicla portas.
     */
    religarQzSeAplicavel: () => Promise<void>
    tipoPainelImpressao: () => "plugin" | "software" | "navegador"
    mandarImprimir: (configs: any) => Promise<void>
    imprimirQz: (configuracao: any) => Promise<void>
    impressao: (configuracoes: any) => void

    pedidosImpressos: Map<number, boolean>
    imprimiuRecentemente: (idPedido: number) => boolean
}

const createImpressaoStore: StateCreator<StoreType, [], [], ImpressaoStoreType> = (set, get) => {
    // Liga a centralização da impressão: elege a aba impressora do navegador e
    // registra quem imprime de fato (este `mandarImprimir`, que na líder não
    // reencaminha — só as demais abas encaminham para cá).
    //
    // Antes de imprimir, a líder GARANTE o QZ. Um job só chega aqui encaminhado por uma
    // aba que tinha QZ vivo (a centralização só vale para o tipo "plugin"), então o QZ
    // Tray está rodando NESTA máquina. Se esta aba nunca conectou — telas fora do modo
    // "Sempre" não pré-conectam —, `tipoPainelImpressao()` devolveria "navegador" e a
    // comanda sairia pelo window.print() de uma aba de fundo (foi o bug do Registrador
    // como aba líder). O teto de tempo mantém o comportamento antigo quando o QZ está
    // realmente fechado: segue e cai no fallback do navegador.
    iniciarImpressoraLider(async configs => {
        if (get().impressorasQZ.length === 0) {
            try {
                await Promise.race([
                    get().buscarImpressoras(),
                    new Promise(resolve => setTimeout(resolve, RECONEXAO_QZ_TIMEOUT_MS)),
                ])
            } catch {
                /* QZ indisponível: segue e cai no fallback do navegador */
            }
        }
        get().mandarImprimir(configs)
    })

    // Detecção confiável de "QZ caiu": ao fechar/errar a conexão, zera o flag e a
    // lista viva de impressoras — sem depender do isActive() (que considera CONNECTING
    // como ativo). Assim, com o QZ fechado, tipoPainelImpressao volta para "navegador"
    // e a impressão cai no fallback em vez de tentar um QZ morto.
    try {
        const aoCairQz = () => {
            qzConexaoViva = false
            if (get().impressorasQZ.length > 0) set({ impressorasQZ: [] })
        }
        qz.websocket.setClosedCallbacks(aoCairQz)
        qz.websocket.setErrorCallbacks(aoCairQz)
    } catch {
        /* ambiente sem qz-tray disponível: ignora */
    }

    return {
        setState: state => set(state),
        impressorasQZ: [],
        buscarImpressoras: async () => {
            // Uma busca por vez (single-flight): vários caminhos chamam isto quase junto
            // (polling do OperacaoTempoReal, pré-conexão do modo automático, a líder antes
            // de imprimir, o "Identificar Printer" do Registrador). Sem isto, dois
            // `connect()` disputavam o mesmo websocket e o que perdia a corrida caía no
            // catch, zerando `impressorasQZ` e marcando falha recente — a aba ficava "sem
            // QZ" mesmo com o módulo aberto, e a comanda caía no navegador.
            if (buscaImpressorasEmAndamento) return buscaImpressorasEmAndamento
            const busca = (async () => {
                try {
                    // Conecta se ainda não há conexão; depois enumera. `await` (em vez do
                    // `.then` solto de antes) garante que uma FALHA de connect/find caia no
                    // catch abaixo e o estado do QZ seja zerado.
                    if (!qz.websocket.isActive()) {
                        await qz.websocket.connect()
                    }
                    const printers = (await qz.printers.find()) as any[]
                    set({ impressorasQZ: printers ?? [] })
                    // Enumerou impressoras = conexão real e utilizável.
                    qzConexaoViva = true
                    return printers
                } catch (error) {
                    console.error(error)
                    // Falha ao conectar/enumerar = QZ indisponível: zera o estado para o painel
                    // cair no fallback do navegador (não ficar preso achando que há QZ ativo).
                    qzConexaoViva = false
                    ultimaFalhaQzEm = Date.now()
                    set({ impressorasQZ: [] })
                    return []
                }
            })()
            buscaImpressorasEmAndamento = busca
            try {
                return await busca
            } finally {
                buscaImpressorasEmAndamento = null
            }
        },
        qzAtivo: () => {
            try {
                // isActive() sozinho não serve: retorna true em CONNECTING (QZ fechado, em
                // reconexão). Exigimos também a conexão confirmada por enumeração
                // (qzConexaoViva), zerada pelos callbacks de fechamento/erro.
                return qzConexaoViva && qz.websocket.isActive()
            } catch {
                return false
            }
        },
        religarQzSeAplicavel: async () => {
            // QZ falhou há pouco? Não tenta reconectar inline — com o QZ fechado o connect
            // cicla portas por vários segundos e seguraria o spinner do aceite/preparo. O
            // polling do Cabeçalho continua tentando reconectar em segundo plano.
            if (Date.now() - ultimaFalhaQzEm < JANELA_SEM_RETENTAR_QZ_MS) return
            try {
                // Teto de tempo: se o QZ voltou, conecta bem antes disso; se está fechado,
                // seguimos sem travar (o pedido já foi aceito/adicionado à lista).
                await Promise.race([
                    get().buscarImpressoras(),
                    new Promise(resolve => setTimeout(resolve, RECONEXAO_QZ_TIMEOUT_MS)),
                ])
            } catch {
                /* QZ indisponível */
            }
        },
        tipoPainelImpressao: () => {
            try {
                // Override por NAVEGADOR: mesmo com o QZ disponível, o usuário pode forçar a
                // impressão nativa do navegador (config "Painel com Impressão"). Guardado em
                // localStorage (por navegador/estação), não sincroniza entre máquinas.
                if (forcarImpressaoNavegador()) return "navegador"

                // LEITURA PURA do estado atual — NÃO dispara conexão com o QZ aqui. Este
                // método é chamado em caminhos quentes (por pedido no aceite/impressão
                // automáticos e até em render). Disparar buscarImpressoras()/connect() a
                // cada chamada gerava uma tempestade de tentativas de WebSocket quando o QZ
                // está fechado, floodando o console e travando a página. A conexão do QZ é
                // mantida pelo polling do Cabeçalho e pelo verificarSeTemQZ do Recebedor.
                if (get().impressorasQZ.length > 0) return "plugin"
                return window.navigator.userAgent.includes("Electron") ? "software" : "navegador"
            } catch (error) {
                console.error(error)
                return "navegador"
            }
        },
        mandarImprimir: async configs => {
            let {
                pedido = {},
                texto = "",
                qtd = 1,
                QZObrigatorio = false,
                numeroDaImpressora = 0,
                isResumo = false,
                // Força a impressão pelo navegador (window.print) mesmo que o tipo detectado
                // seja "plugin"/QZ — usado como fallback quando o QZ está indisponível.
                forcarNavegador = false,
            } = configs

            // Resolve o tipo de saída já aqui: ele decide, inclusive, se a centralização
            // entre abas se aplica.
            const tipo = forcarNavegador ? "navegador" : get().tipoPainelImpressao()

            // [IMPRESSAO-DEBUG] Identificadores para diferenciar "mesma comanda 2x"
            // (duplicidade real) de vias legítimas (dois pedidos, alternada por setor,
            // reimpressão). `pedido.id` = SolicitacaoImpressao.id quando vem do SSE
            // (processarEventoImpressao passa `pedido: evento`); os demais ajudam a
            // amarrar as duas chamadas ao MESMO pedido físico.
            logImp(
                "mandarImprimir chamado. tipo:",
                tipo,
                "| souLider:",
                souImpressoraLider(),
                "| solicitacaoId:",
                (pedido as any)?.id ?? "(sem id)",
                "| correlationId:",
                (pedido as any)?.correlationId ?? (pedido as any)?.pedidoId ?? "(sem)",
                "| shortRef:",
                (pedido as any)?.shortReference ?? "(sem)",
                "| impressora:",
                numeroDaImpressora,
                "| isResumo:",
                isResumo,
                "| qtd:",
                qtd,
                "| textoLen:",
                (texto ?? "").length
            )

            // Centralização (aba impressora-líder) vale SÓ para o QZ: sem ela, a mesma
            // comanda sairia 2x na MESMA impressora física quando há várias abas abertas
            // (aceite × push da impressão alternada). Na impressão pelo navegador
            // (window.print) cada aba abre o próprio diálogo local — não há impressora
            // compartilhada para duplicar —, então cada aba imprime a SUA e não encaminha
            // para a líder.
            if (tipo === "plugin" && encaminharImpressao(configs)) {
                logImp(
                    "→ ENCAMINHADO para a aba líder (esta aba NÃO imprime). solicitacaoId:",
                    (pedido as any)?.id ?? "(sem id)",
                    "| impressora:",
                    numeroDaImpressora
                ) // [IMPRESSAO-DEBUG]
                return
            }

            const configuracoes = get().configuracoes

            // Trava de segurança: nunca imprime a comanda de um pedido em status oculto/não
            // confirmado (-5 FALHA_PAGAMENTO, 77 AGUARDANDO_PAGAMENTO, 99 CADASTRADO). Protege
            // contra eventos/rotas antigas (ex.: build izzabot desatualizado) que disparam a
            // impressão antes da confirmação. Checa os dois campos porque o evento SSE traz
            // `statusPedido` e a impressão local traz `status`. Não vale para o resumo do dia
            // (não é comanda de pedido).
            const statusPedido = Number(pedido?.status ?? pedido?.statusPedido)
            const statusSemImpressao =
                Number.isFinite(statusPedido) && STATUS_OCULTOS_SEM_IMPRESSAO.includes(statusPedido)
            if (!isResumo && statusSemImpressao) {
                console.warn(
                    `[IMPRESSAO] comanda BLOQUEADA: pedido ${
                        pedido?.id ?? pedido?.correlationId ?? "?"
                    } em status oculto/não confirmado (${statusPedido})`
                )
                return
            }

            console.log("mandar imprimir", configs)
            if (configuracoes!.user!.parametros.assinaturaLimitada === true) {
                alertStart(
                    "Não foi impresso, porque sua assinatura está limitada por inadimplência.",
                    "error"
                )
                console.error(
                    "Não foi impresso, porque sua assinatura está limitada por inadimplência."
                )
                return
            }

            // Barreira para impressão alternada, remover futuramente
            const impressaoAlternada = configuracoes!.user!.parametros.impressaoAlternada

            // Parâmetro customizado para habilitar o uso da nova forma de impressão
            const customParams = JSON.parse(localStorage.getItem("custom-params") ?? "{}")
            // const usarImpressaoNova = customParams.PUBLIC_IMPRESSAO_NOVA == true
            const usarImpressaoNova = false

            if (configs.pedido?.shortReference && !impressaoAlternada && usarImpressaoNova) {
                const configImpressao = getConfiguracoesDeTextoLocal()
                const tipoImpressao = configuracoes?.printer?.tipoImpressao
                const printer = configuracoes?.printer
                const parametros = configuracoes?.user?.parametros

                let configChanged = false

                if (!configImpressao.parametrosGerais.negrito && printer?.negrito) {
                    configImpressao.parametrosGerais.negrito = true
                    configChanged = true
                }

                if (
                    !configImpressao.parametrosGerais.textoInicioLinha &&
                    parametros?.espacoInicioLinha
                ) {
                    configImpressao.parametrosGerais.textoInicioLinha = " "
                    configChanged = true
                }

                let configParaGerar = configImpressao

                if (tipoImpressao === "html") {
                    if (printer?.tamanhoFonteImpressao) {
                        const px = Number(printer.tamanhoFonteImpressao)
                        const fonteRuntime = px >= 22 ? 2 : px >= 17 ? 1 : 0
                        configParaGerar = {
                            ...configImpressao,
                            parametrosGerais: {
                                ...configImpressao.parametrosGerais,
                                fonte: fonteRuntime,
                            },
                        }
                    }
                } else if (
                    configImpressao.parametrosGerais.fonte === 0 &&
                    printer?.tamanhoFonteSelect === "grande"
                ) {
                    configImpressao.parametrosGerais.fonte = 2
                    configChanged = true
                }

                if (configChanged) setConfiguracoesDeTextoLocal(configImpressao)

                texto =
                    tipoImpressao === "html"
                        ? gerarCorpoHTML(configs.pedido, configParaGerar)
                        : gerarTextoImpressao(configs.pedido, configImpressao)
                configs.texto = texto
            }

            // Modo teste de impressão: NÃO imprime de verdade — só registra no console a
            // comanda que SERIA impressa. Serve para testar o fluxo (qual comanda sai em cada
            // situação, ex.: item adicionado a mesa) SEM impressora/QZ ligados.
            // Duas chaves ligam isso:
            //  - localStorage["logImpressao"]: SÓ loga; NÃO muda o ambiente. Use esta no teste
            //    cruzado celular→computador (os dois precisam ficar no MESMO ambiente para o
            //    pedido do celular chegar no computador via SSE).
            //  - localStorage["teste"]: também loga, mas essa chave JÁ aponta o app para a
            //    pré-produção — só use se celular e computador estiverem ambos em pré-produção.
            // Remova a chave para voltar a imprimir normalmente.
            try {
                if (localStorage.getItem("logImpressao") || localStorage.getItem("teste")) {
                    const idTeste = pedido?.id ?? pedido?.correlationId ?? pedido?.pedidoId ?? "?"
                    const alternadaTeste =
                        !isResumo && !!configuracoes?.user?.parametros?.impressaoAlternada
                    console.log(
                        `%c🖨️ [TESTE] Comanda que SERIA impressa`,
                        "color:#c0392b;font-weight:bold"
                    )
                    console.log(
                        `[TESTE] pedido ${idTeste} | tipo ${tipo} | impressora ${numeroDaImpressora} | vias ${qtd}${
                            alternadaTeste ? " | alternada" : ""
                        }`
                    )
                    if (alternadaTeste) {
                        for (let i = 1; i <= 10; i++) {
                            const t = pedido?.["texto" + i]
                            if (t) console.log(`[TESTE] ── impressora ${i} ──\n${t}`)
                        }
                        if (pedido?.texto)
                            console.log(`[TESTE] ── comanda completa ──\n${pedido.texto}`)
                    } else {
                        console.log(`[TESTE] ── comanda ──\n${texto || pedido?.texto || "(vazio)"}`)
                    }
                }
            } catch {
                /* best-effort: o modo teste nunca pode quebrar a impressão */
            }

            try {
                // [IMPRESSAO-DEBUG]
                logImp(
                    "tipo resolvido:",
                    tipo,
                    "| impressorasQZ(vivas):",
                    get().impressorasQZ.length,
                    "| forcarNavegador(ls):",
                    forcarImpressaoNavegador(),
                    "| QZObrigatorio:",
                    QZObrigatorio,
                    "| texto vazio?:",
                    !texto
                )
                switch (tipo) {
                    // "software" (app desktop/Electron) cai aqui junto com "navegador": o tipo
                    // só vale "software" quando NÃO há QZ (com QZ o tipo seria "plugin"), e não
                    // existe ponte de impressão com o Electron neste código. Antes era um `break`
                    // vazio, então no app desktop sem QZ o clique em imprimir não fazia nada e
                    // não avisava nada. window.print() funciona no Electron.
                    case "software":
                    case "navegador":
                        if (!QZObrigatorio) {
                            // Impressão pelo navegador SEMPRE numa janela separada (nova aba):
                            // window.print() na própria página congela o painel inteiro (e
                            // drivers ruins travam a pré-visualização). Na janela dedicada só ela
                            // bloqueia; o painel segue livre e a aba se fecha ao terminar.
                            // As N vias vão num único conteúdo, com quebra de página entre elas.
                            const corpo = texto.replace(/(?:\r\n|\r|\n)/g, "<br>")
                            const vias = configuracoes!.user!.parametros.agruparViasImpressao
                                ? 1
                                : Math.max(1, Number(qtd) || 1)
                            let htmlVias = ""
                            for (let j = 0; j < vias; j++) {
                                const quebra = j < vias - 1 ? ' class="quebra-pagina"' : ""
                                htmlVias += `<div${quebra}>${corpo}</div>`
                            }
                            // Tamanho de fonte configurável (mesma opção do #rootPrint).
                            const tamanhoFonte =
                                configuracoes!.printer.tamanhoFonteNavegador === "grande"
                                    ? "22px"
                                    : "18px"
                            logImp("navegador: imprimindo em nova aba (vias:", vias, ")") // [IMPRESSAO-DEBUG]
                            imprimirEmNovaAba(htmlVias, tamanhoFonte)
                        }
                        break

                    case "plugin":
                        if (numeroDaImpressora === 0) {
                            const impressorasConfiguradas = configuracoes!.printer
                                .impressoras as string[]

                            if (!impressorasConfiguradas.length) {
                                // QZ conectado, porém nenhuma impressora foi escolhida na
                                // configuração. Em vez de lançar um erro cru (que ia parar no
                                // Sentry e não orientava ninguém), mostramos um popup claro
                                // apontando para a configuração de impressão.
                                alertStart(
                                    "Nenhuma impressora configurada. Na tela de pedidos, clique na engrenagem (⚙️ Configurações) e selecione uma impressora.",
                                    "warning"
                                )
                                return
                            }

                            let primeiroTextoAlternadoNaoImpresso = 0
                            let algumTextoAlternadoImpresso = false
                            impressorasConfiguradas.forEach(async (impressora, i) => {
                                configs.impressora = impressora

                                if (
                                    !isResumo &&
                                    configuracoes!.user!.parametros.impressaoAlternada
                                ) {
                                    const textoImpressaoAlternada = pedido["texto" + (i + 1)]

                                    // Se for impressão alternada, imprime somente na impressora que houver produtos referentes a ela
                                    if (!textoImpressaoAlternada) {
                                        console.warn(
                                            `Impressão Alternada para impressora Nº${
                                                i + 1
                                            } falhou ao imprimir o pedido ${
                                                pedido.id
                                            }, pois não há o texto referente`
                                        )
                                        return
                                    }

                                    primeiroTextoAlternadoNaoImpresso = i + 1
                                    configs.texto = textoImpressaoAlternada
                                    algumTextoAlternadoImpresso = true
                                }

                                await get().imprimirQz(configs)
                            })

                            // Impressão da comanda completa para impressão alternada
                            const imprimiuComandaCompleta =
                                configuracoes!.user!.parametros.impressaoAlternada &&
                                configuracoes!.printer.imprimirComandaCompleta
                            if (imprimiuComandaCompleta) {
                                configs.impressora =
                                    configuracoes!.printer.impressoraComandaCompleta
                                configs.texto = pedido.texto

                                await get().imprimirQz(configs)
                            }

                            // Se a impressão alternada está ativa mas o pedido não tinha texto
                            // para NENHUMA impressora (e não saiu a comanda completa), nada foi
                            // impresso: deixa isso evidente na tela do Recebedor.
                            if (
                                !isResumo &&
                                configuracoes!.user!.parametros.impressaoAlternada &&
                                !algumTextoAlternadoImpresso &&
                                !imprimiuComandaCompleta
                            ) {
                                alertStart(
                                    `O pedido ${pedido.id} não foi impresso: a impressão alternada está ativada, mas o pedido não possui texto para nenhuma das impressoras configuradas. Verifique a configuração da impressão alternada.`,
                                    "error"
                                )
                            }

                            // Checa se hovue textos não impressos por falta de impressora
                            if (pedido["texto" + (primeiroTextoAlternadoNaoImpresso + 1)]) {
                                console.error(
                                    `Não foi possível completar a Impressão Alternada, pois não há impressora Nº${
                                        primeiroTextoAlternadoNaoImpresso + 1
                                    }`
                                )
                                // alertStart(
                                //     `Não foi possível completar a Impressão Alternada, pois não há impressora Nº${primeiroTextoAlternadoNaoImpresso + 1
                                //     }`,
                                //     "warning"
                                // )

                                let maiorTextoImpressao = 0
                                for (let i = primeiroTextoAlternadoNaoImpresso + 1; true; i++) {
                                    if (pedido["texto" + i]) maiorTextoImpressao = i
                                    else break
                                }

                                for (
                                    let i = primeiroTextoAlternadoNaoImpresso + 1;
                                    i <= maiorTextoImpressao;
                                    i++
                                ) {
                                    get().mandarImprimir({
                                        texto: pedido["texto" + i],
                                        numeroDaImpressora: 1,
                                    })
                                }
                            }
                        } else {
                            const impressorasConfiguradas = configuracoes!.printer
                                .impressoras as string[]
                            const impressora = impressorasConfiguradas[numeroDaImpressora - 1]

                            if (!impressora)
                                throw new Error(`${numeroDaImpressora}ª Impressora não selecionada`)

                            configs.impressora = impressora

                            if (configuracoes!.user!.parametros.impressaoAlternada) {
                                const textoImpressaoAlternada = pedido["texto" + numeroDaImpressora]

                                if (textoImpressaoAlternada) configs.texto = textoImpressaoAlternada
                            }

                            await get().imprimirQz(configs)
                        }
                        break

                    default:
                        break
                }
            } catch (error) {
                console.error(error)
                RegistrarLogErro(error, "mandarImprimir", configs)
            }
        },
        imprimirQz: async (configuracao: any) => {
            let { impressora = "" } = configuracao

            try {
                if (qz.websocket.isActive()) {
                    if (!impressora || impressora == "vazio") {
                        impressora = await qz.printers.getDefault()
                    }

                    configuracao.impressora = impressora

                    return get().impressao(configuracao)
                }

                qz.websocket
                    .connect()
                    .then(() => {
                        if (!impressora) impressora = qz.printers.getDefault()

                        return impressora
                    })
                    .then((printer: string) => {
                        configuracao.impressora = printer

                        get().impressao(configuracao)
                    })
                    .catch((err: any) => {
                        console.error("erro", err)
                        RegistrarLogErro(err, "imprimirQz", configuracao)
                    })
            } catch (error) {
                RegistrarLogErro(error, "imprimirQz", configuracao)
            }
        },
        impressao: configs => {
            try {
                configs.qz = qz
                configs.printer = get().configuracoes!.printer

                const configTexto = getConfiguracoesDeTextoLocal()
                const exibirLogo =
                    get().configuracoes!.printer.imprimirLogo ||
                    configTexto?.parametrosGerais?.exibirLogo === true
                if (exibirLogo)
                    configs.logo = get().configuracoes!.user!.parametros.estabelecimento?.urlLogo

                const tipoImpressao = get().configuracoes!.printer.tipoImpressao || "code"
                if (tipoImpressao == "code") imprimirCode(configs)
                else imprimirHTML(configs)

                if (configs.pedido?.id) get().pedidosImpressos.set(configs.pedido.id, true)
            } catch (error) {
                console.error(error)
                alertStart((error as any).message, "error")
                RegistrarLogErro(error, "impressao", configs)
            }
        },
        pedidosImpressos: new Map(),
        imprimiuRecentemente: idPedido => get().pedidosImpressos.get(idPedido) ?? false,
    }
}

export default createImpressaoStore
